import { API } from '@/modules/api'
import type { FamilyUserInvoice, UnpaidInvoice } from '@/types/familyUserInvoice'
import type { TFamilyUserInvoiceCartLine } from '@/types/cart'
import { OrderItemType, OrderItemTypeCode } from '@/types/orderItemType'
import { defineStore } from 'pinia'
import { useCartStore } from '@/stores/cart'

export const createUniqueId = (invoiceHeaderId: number, orderItemTypeId: number) =>
  `${invoiceHeaderId}::${orderItemTypeId}`

export const useFamilyUserInvoiceStore = defineStore('familyUserInvoice', {
  state: () => ({
    isLoaded: {} as Record<TDistrictId, boolean>,
    invoices: {} as Record<TDistrictId, FamilyUserInvoice[]>,
    unpaidInvoices: {} as Record<TDistrictId, UnpaidInvoice[]>
  }),

  getters: {
    isLoading(state) {
      return (districtId: TDistrictId) => !(state.isLoaded[districtId] ?? false)
    },
    getInvoices(state) {
      return (districtId: TDistrictId) => state.invoices[districtId] ?? []
    },
    getUnpaidInvoices(state) {
      return (districtId: TDistrictId) => state.unpaidInvoices[districtId] ?? []
    }
  },

  actions: {
    async fetch(_districtId: TDistrictId) {
      this.isLoaded[_districtId] = false
      const unpaidInvoice = await API.Get.unpaidInvoices()
      this.unpaidInvoices[_districtId] = unpaidInvoice
        .filter(i => i.districtId === _districtId)
        .filter(i => i.contractRevision === null)
      this.validateCart(_districtId)
      this.isLoaded[_districtId] = true
    },

    // This function is used to validate the cart lines for the family user invoice store
    // It checks if the cart lines are valid compared to the data from the API, and if not, it removes them from the cart.
    // This prevents the cart from attempting to get order line estimates or checking out with invalid data.
    validateCart(districtId: TDistrictId) {
      const cartStore = useCartStore()

      for (const line of cartStore.getCartLines(districtId) as TFamilyUserInvoiceCartLine[]) {
        // Every invoice line this store owns, not just tuition ones. The section
        // adds each line under the invoice's OWN order item type, so a childcare or
        // other-fee line never matched the old tuition-only condition and was never
        // pruned once the invoice was paid or deleted elsewhere — leaving a stale
        // line that tries to pay an already-settled invoice. Tuition lines take the
        // same path they always did. [DTA-671]
        if (line.isInvoice === true && line.invoiceHeaderId !== undefined) {
          const invoice = this.unpaidInvoices[districtId].find(invoice => {
            return (
              createUniqueId(
                invoice.invoiceHeaderId,
                line.orderItemTypeId ?? OrderItemType.AdditionalCharge
              ) === line.uniqueId
            )
          })
          // ensure fee exists
          if (!invoice) {
            console.log('family userinvoice not found, removing from cart', line.invoiceHeaderId)
            this.removeFromCart(districtId, line.invoiceHeaderId, line.orderItemType)
          }
          // ensure fee amount is less than cart amountn meaning cart amount is too high
          if (invoice && invoice.totalAmount < line.amount) {
            console.log(
              'invoice amount is less than cart amount, removing from cart',
              line.invoiceHeaderId
            )
            this.removeFromCart(districtId, line.invoiceHeaderId, line.orderItemType)
          }
        }
      }
    },

    addToCart(districtId: TDistrictId, invoiceData: Omit<TFamilyUserInvoiceCartLine, 'uniqueId'>) {
      const cartStore = useCartStore()
      const uniqueId = createUniqueId(invoiceData.invoiceHeaderId, invoiceData.orderItemTypeId)
      let existing = cartStore.getCartLine(districtId, uniqueId)
      if (existing) {
        cartStore.updateCartLine(districtId, uniqueId, invoiceData)
      } else {
        cartStore.addCartLine(districtId, uniqueId, {
          ...invoiceData,
          uniqueId
        })
      }
    },

    updateCartLine(
      districtId: TDistrictId,
      invoiceData: Partial<TFamilyUserInvoiceCartLine> & {
        invoiceHeaderId: number
        orderItemTypeId: number
      }
    ) {
      const cartStore = useCartStore()
      const uniqueId = createUniqueId(invoiceData.invoiceHeaderId, invoiceData.orderItemTypeId)
      cartStore.updateCartLine(districtId, uniqueId, invoiceData)
    },

    removeFromCart(districtId: TDistrictId, invoiceHeaderId: number, orderItemTypeId: number) {
      const cartStore = useCartStore()
      const uniqueId = createUniqueId(invoiceHeaderId, orderItemTypeId)
      cartStore.removeCartLine(districtId, uniqueId)
    }
  }
})
